/**
 * THE SDH / SPEAKER / MUSIC / WATERMARK PATTERN LIBRARY.
 *
 * `RemoveSdhOp` carries `soundEffectPatterns`, `musicPatterns` (contract 1.6.0) and
 * `watermarks` as `SerialRegex[]` — inert JSON, per §10 — so this file is the DATA the UI
 * offers and the presets ship, not machinery.
 * Every entry states what it matches, where it came from, and what it will eat when it is
 * wrong. `defaultOn` is a PRODUCT decision and is escalated as one: a default-on watermark
 * pattern that eats a line of real dialogue is a support problem and a trust problem.
 *
 * Matching runs against `flattenCue(cue).text` — the contract freezes that, so line breaks
 * are visible as '\n' and a pattern spanning a break ('[MAN SPEAKING\nIN SPANISH]') is one
 * match. Every pattern here is written with that in mind: `^` and `$` are used with the `m`
 * flag to mean line-within-cue.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * WHAT IS REGEX-ABLE AND WHAT NEEDS `CueKind`
 *
 * Safely done by regex on flattened cue text: bracketed effects, music-note wrappers,
 * `NAME:` prefixes, `>>` markers, watermark/credit lines, bare-uppercase effect lines.
 * All of them are LOCAL, and all of them are visible in the text itself.
 *
 * NOT safely done by regex, and therefore reliant on `CueKind` assigned at parse (CONVERT)
 * or by `ResegmentOp.classifyKinds`:
 *   • a WHOLE-cue effect with no brackets and no caps ('birds chirping' on its own cue)
 *   • a music cue whose lyric the user may want KEPT — '♪ lyric ♪' is kind 'music', and the
 *     difference between "drop the cue" and "drop the notes and keep the lyric" is a
 *     property of the cue, not of the string
 *   • distinguishing a positioned ASS sign (kind 'sign') from dialogue, which is what stops
 *     bracket-stripping from eating a typeset sign's text
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

import type { BracketPair, SerialRegex } from '../../../contracts/cue-model.ts';

export interface PatternEntry {
  /** Stable machine id — presets and saved sessions reference this, never the index. */
  readonly id: string;
  /** UI label. DECISION_NEEDED: everything a user reads. */
  readonly label: string;
  readonly re: SerialRegex;
  /** DECISION_NEEDED: whether this ships enabled. */
  readonly defaultOn: boolean;
  /**
   * ONE LINE UNDER THE CHECKBOX. User-facing, US spelling, and deliberately separate from
   * `falsePositives`: that one is written for the founder deciding what ships on, and reads
   * like an internal note because it is one.
   */
  readonly help: string;
  /** What it eats when it is wrong. Written for the founder, not for the compiler. */
  readonly falsePositives: string;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Brackets

  Region and language conventions, and which of them carry SDH meaning:

    [door slams]   US / UK captioning, the dominant convention                  SDH
    (door slams)   US captioning (CEA-608 legacy) and most European subtitling  SDH
    （笑）          CJK full-width parens — laughter, effects, speaker names      SDH
    【】            CJK lenticular — headings, on-screen labels, sometimes SDH    MIXED
    「」『』        JAPANESE QUOTATION MARKS — ordinary quoted dialogue            NOT SDH
    ≪≫ 《》         CJK guillemets — titles and quotes                            NOT SDH

  `BracketPair` in the contract offers '{}', '()', '[]', '**', '##', '<>', '""', '（）' and
  '【】' — and deliberately NOT '「」'. That is the right call and it is worth stating: a
  bracket-stripper that eats Japanese quotation marks is a bug that only shows up in
  Japanese, and by the time anyone notices, every Japanese file processed since has lost its
  quoted dialogue.
───────────────────────────────────────────────────────────────────────────────────────*/

/** Pairs the cleaner preset enables by default. '{}' is NOT here — see the note below. */
export const DEFAULT_BRACKET_PAIRS: readonly BracketPair[] = ['[]', '()'];

/**
 * '{}' is offered by `StripDelimitedOp` and must never be on by default: an ASS file is full
 * of `{\i1}`, `{\pos(640,80)}` and `{\t(0,500,\frz30)}`. Those are parsed into `InlineStyle`,
 * `Animation`, `MoveSpec` and `UnknownTag` at parse time and `TextRun.text` never contains
 * them, so `{}`-stripping structurally cannot reach them — but "structurally it cannot" is
 * what everyone says before it does, which is why the proof is a test rather than a comment.
 */
export const CURLY_BRACES_ARE_OPT_IN = true;

/*───────────────────────────────────────────────────────────────────────────────────────
  Speaker labels
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * Words that look like speaker labels and are not. The discriminator that matters most:
 * a real speaker label is a NAME, and these are not names. Checked case-insensitively
 * against the token before the colon.
 */
export const NOT_SPEAKER_WORDS: readonly string[] = [
  'note',
  'notes',
  'warning',
  'caution',
  'chapter',
  'part',
  'act',
  'scene',
  'title',
  'subtitle',
  'subtitles',
  'translation',
  'translated',
  'translator',
  'sync',
  'synced',
  'corrected',
  'edited',
  'source',
  'from',
  'to',
  'cc',
  'bcc',
  're',
  'ps',
  'nb',
  'eg',
  'ie',
  'http',
  'https',
  'www',
  'tel',
  'fax',
  'email',
  'e-mail',
  'ref',
  'id',
  'isbn',
  'doi',
];

/**
 * `NAME:` at the start of a line.
 *
 * The shapes this must cover, all of them attested:
 *   JOHN:            plain all-caps
 *   JOHN (V.O.):     with a parenthetical (V.O., O.S., CONT'D, on TV)
 *   [JOHN]:          bracketed
 *   - JOHN:          preceded by a dialogue dash
 *   >> JOHN:         US broadcast speaker-change marker plus a name
 *   John Smith:      mixed case, up to three words
 *   MRS. O'BRIEN:    with a period and an apostrophe
 *
 * The false-positive class it must NOT eat:
 *   'Note: this is not a speaker'   → NOT_SPEAKER_WORDS
 *   '12:30'                          → the label may not be digits only, and the colon may
 *                                      not be followed immediately by a digit
 *   'Mr. Smith: a biography'         → indistinguishable by shape alone. The rule that
 *                                      settles it is POSITION: a speaker label is at the
 *                                      START of a line, and a caption that opens with a
 *                                      title-cased name and a colon is a speaker label far
 *                                      more often than it is a book title. Users who
 *                                      disagree turn `speakerLabels` off; that is what the
 *                                      checkbox is for.
 *
 * The leading dash and the `>>` are captured in group 1 and PUT BACK by the op: a dialogue
 * dash is punctuation that must survive, and only the label is removed.
 */
export const SPEAKER_LABEL: SerialRegex = {
  source:
    '^([ \\t]*(?:[-–—―][ \\t]*)?)' + // 1: the dialogue dash, PRESERVED — it is punctuation
    '(?:>>[ \\t]*)?' + // the US broadcast marker is part of the label and goes with it
    '(?:\\[)?' +
    "((?:[A-ZÀ-ÞĀ-Ž0-9][A-ZÀ-ÞĀ-Ž0-9'’.\\-]*|[A-ZÀ-ÞĀ-Ž][a-zà-þā-ž'’.\\-]+)" +
    "(?:[ \\t]+(?:[A-ZÀ-ÞĀ-Ž0-9][A-ZÀ-ÞĀ-Ž0-9'’.\\-]*|[A-ZÀ-ÞĀ-Ž][a-zà-þā-ž'’.\\-]+)){0,2})" +
    '(?:\\])?' +
    '(?:[ \\t]*\\([^)\\n]{1,24}\\))?' +
    '[ \\t]*:(?=[ \\t]|$)[ \\t]*',
  flags: 'gm',
};

/** `>>` on its own — US broadcast speaker change with no name. */
export const SPEAKER_CHANGE_MARKER: SerialRegex = { source: '^[ \\t]*>>[ \\t]*', flags: 'gm' };

/**
 * Japanese name-in-parens: `（ジョン）` at the start of a line, the standard JA captioning
 * convention for attributing a line to a speaker. Distinct from `（笑）`-style effects only
 * by position, so it is scoped to line start.
 */
export const SPEAKER_LABEL_JA: SerialRegex = {
  source: '^[ \\t]*（[^）\\n]{1,12}）[ \\t]*',
  flags: 'gm',
};

/*───────────────────────────────────────────────────────────────────────────────────────
  Music
───────────────────────────────────────────────────────────────────────────────────────*/

/** Every music-note codepoint that appears in subtitle files. */
export const MUSIC_NOTE_CHARS = '♪♫♬♩\u{1D15E}\u{1D160}';

export const MUSIC_PATTERNS: readonly PatternEntry[] = [
  {
    id: 'music-note-wrapped',
    label: 'Music notes around a line',
    // ♪ lyric ♪ / ♪♪ lyric ♪♪ — strips the NOTES and keeps the lyric. Whether the lyric
    // itself goes is `dropCueIfEmpty` plus the cue's own kind, never this pattern.
    //
    // The notes alone, with no spaces: this is the exact regex `musicNotes` applied before the
    // op had a pattern list (contract 1.6.0), so a page left at its defaults writes the same
    // file it always did. The space beside a note is still tidied, by `swallowSpace` in the
    // op's deletion. Matching it here as well ('[♪]+[ \t]*|[ \t]*[♪]+', as this read before)
    // took a line's leading space too, which the old op never did.
    re: { source: `[${MUSIC_NOTE_CHARS}]+`, flags: 'gu' },
    defaultOn: true,
    help: 'The notes are removed and the lyric stays.',
    falsePositives:
      'A lyric line that legitimately prints a note character as content. Vanishingly rare; ' +
      'the notes are decoration in every caption file examined.',
  },
  {
    id: 'music-bracketed',
    label: '[MUSIC] / [MUSIC PLAYING] cues',
    re: {
      source:
        '[\\[(（【][ \\t]*(?:MUSIC|MUSIC PLAYING|UPBEAT MUSIC|DRAMATIC MUSIC|THEME MUSIC|' +
        'MUSIC CONTINUES|SINGING|音楽|音乐|BGM)[^\\])）】\\n]*[\\])）】]',
      flags: 'gi',
    },
    // OFF, and not because of false positives. Before contract 1.6.0 this box reached nothing,
    // so what users got was the note characters alone, plus `[MUSIC]` whenever the bracketed
    // sound-effect pattern happened to be on. Shipping it ON now would start deleting 【BGM】
    // from untouched runs, and `[MUSIC]` from runs with sound effects off: a change to every
    // file nobody asked for. Ticked, it removes them whatever the sound-effect boxes say.
    defaultOn: false,
    help:
      'Off by default: the bracketed sound effects above already remove [MUSIC] and (MUSIC). ' +
      'Tick it to remove them with sound effects off, or in full-width brackets like 【BGM】.',
    falsePositives:
      'Dialogue that quotes a bracketed word "MUSIC". Not observed; the bracket plus the ' +
      'keyword is a strong pair.',
  },
  {
    id: 'music-hash',
    label: 'ASCII # used as a music note (older files)',
    // Older SRT rips substitute '#' for ♪. Restricted to a '#' that OPENS or CLOSES a line,
    // because '#' inside a line is a hashtag or a number sign far more often than a note.
    re: { source: '^[ \\t]*#+[ \\t]*|[ \\t]*#+[ \\t]*$', flags: 'gm' },
    defaultOn: false,
    help: 'Off by default: a trailing # is a hashtag far more often than a note.',
    falsePositives:
      'Eats a trailing hashtag ("...subscribe #ad") and a leading issue number ("#42 was ' +
      'the one"). This is why it ships OFF: the note reading is a legacy convention and the ' +
      'hashtag reading is the modern one.',
  },
];

/*───────────────────────────────────────────────────────────────────────────────────────
  Sound effects
───────────────────────────────────────────────────────────────────────────────────────*/

export const SOUND_EFFECT_PATTERNS: readonly PatternEntry[] = [
  {
    id: 'sfx-bracketed',
    label: 'Bracketed effects — [door slams], (sighs)',
    // '\n' is NOT excluded from the interior: `flattenCue` materialises line breaks precisely
    // so that '[MAN SPEAKING\nIN SPANISH]' is ONE match rather than two unmatched brackets.
    // The length cap is what stops an unclosed bracket from swallowing the rest of the cue.
    re: { source: '[\\[][^\\]]{0,80}[\\]]|[(][^)]{0,80}[)]', flags: 'g' },
    defaultOn: true,
    help: 'Round brackets also hold asides in dialogue, so each pair can be turned off separately.',
    falsePositives:
      'Parenthetical ASIDES in dialogue ("I told him (twice) to stop"). Real, and the reason ' +
      "'()' can be turned off separately from '[]' in the bracket list.",
  },
  {
    id: 'sfx-bracketed-cjk',
    label: 'Full-width bracketed effects — （笑）【拍手】',
    re: { source: '[（][^）]{0,40}[）]|[【][^】]{0,40}[】]', flags: 'g' },
    defaultOn: false,
    help: 'Off by default: full-width brackets also carry speaker names in Japanese.',
    falsePositives:
      'Full-width parens also carry speaker names in Japanese and ordinary parentheticals in ' +
      'Chinese. 【】 is a heading marker as often as an effect. Ships OFF; the CJK presets ' +
      'turn it on.',
  },
  {
    id: 'sfx-bare-caps',
    label: 'Bare all-caps effect lines — DOOR SLAMS',
    // A WHOLE line, at least two words, all caps, no sentence punctuation, not ending in a
    // colon (that is a speaker label), and not a single token (that is an acronym or a
    // shouted word).
    re: {
      source: "^[ \\t]*[A-ZÀ-ÞĀ-Ž][A-ZÀ-ÞĀ-Ž'’\\- ]*[ \\t]+[A-ZÀ-ÞĀ-Ž][A-ZÀ-ÞĀ-Ž'’\\-]*[ \\t]*$",
      flags: 'gm',
    },
    defaultOn: false,
    help: 'Off by default: all-caps dialogue looks exactly the same.',
    falsePositives:
      'THE big one. All-caps dialogue ("GET OUT OF THE CAR"), acronym-heavy lines ("NASA JPL ' +
      'CONFIRMS"), and titles burned into the caption stream are all shaped identically. ' +
      'Ships OFF, and the recommendation is that it stays off: the only reliable separator ' +
      'is CueKind, which the parser assigns from position and file context.',
  },
];

/*───────────────────────────────────────────────────────────────────────────────────────
  Watermarks and credits

  WHERE THEY SIT: overwhelmingly the FIRST cue and the LAST cue — a ripper's credit and a
  site advert. Mid-file occurrences exist (a per-reel credit on a two-CD rip, an advert every
  ~20 minutes on some sites) so nothing here is position-scoped; the patterns are specific
  enough not to need it.
───────────────────────────────────────────────────────────────────────────────────────*/

export const WATERMARK_PATTERNS: readonly PatternEntry[] = [
  {
    id: 'wm-credit-by',
    label: 'Subtitles by / Sync by / Translated by …',
    re: {
      source:
        '^[ \\t]*(?:sub(?:title)?s?|caption(?:ing|s)?|sync(?:hroni[sz]ation)?|transcript(?:ion)?|' +
        'translation|translated|corrected|resync(?:ed)?|ripped|encoded|timed|proofread)' +
        '[ \\t]*(?:by|:|-|–|—|par|von|de)[ \\t]*[^\\n]{0,80}$',
      flags: 'gim',
    },
    defaultOn: true,
    help: 'Whole lines crediting whoever made or synced the file.',
    falsePositives:
      'A line of dialogue that begins "Translated by" — possible in a documentary. Low, and ' +
      'the whole-line anchor keeps it from eating a clause.',
  },
  {
    id: 'wm-download-site',
    label: 'Download-site credits (OpenSubtitles, Addic7ed, YIFY, …)',
    re: {
      source:
        '^[^\\n]*\\b(?:opensubtitles|addic7ed|subscene|yify|yts|podnapisi|napisy|legendas|' +
        'subdivx|tvsubtitles|subtitleseeker|allsubs|isubtitles)\\b[^\\n]*$',
      flags: 'gim',
    },
    defaultOn: true,
    help: 'Site names that do not appear in real dialogue.',
    falsePositives: 'Essentially none — these are brand names that do not occur in dialogue.',
  },
  {
    id: 'wm-url',
    label: 'Bare URLs and domains',
    re: {
      source:
        '^[^\\n]*(?:https?://|www\\.)[^\\s\\n]+[^\\n]*$|' +
        '^[ \\t]*[a-z0-9-]+\\.(?:com|net|org|info|tv|co|io|me|ru|es|fr|de|nl|br)[ \\t]*$',
      flags: 'gim',
    },
    defaultOn: true,
    help: 'Check the preview first — a web address someone actually says is removed too.',
    falsePositives:
      'A URL spoken or shown as content ("visit example.com for the recipe") is eaten. Real ' +
      'and not rare in tutorial content — this is the pattern most likely to need a preview ' +
      'before it runs, which is what the diff view is for.',
  },
  {
    id: 'wm-advert',
    label: 'Advertising lines ("Advertise your product here", "Become VIP")',
    re: {
      source:
        '^[^\\n]*\\b(?:advertise your product|advertise here|api\\.opensubtitles|' +
        'become (?:a )?vip member|support us and become|remove all ads|watch (?:any|all) video' +
        ' online|download (?:free )?subtitles?)\\b[^\\n]*$',
      flags: 'gim',
    },
    defaultOn: true,
    help: 'Boilerplate that subtitle sites add to their downloads.',
    falsePositives: 'None observed; each phrase is boilerplate from a specific site.',
  },
  {
    id: 'wm-release-group',
    label: 'Release-group tags — [GROUP], -GROUP, {GROUP}',
    // A whole line that is only a bracketed or dash-prefixed uppercase token.
    re: {
      source: '^[ \\t]*(?:[\\[{(][A-Za-z0-9._\\-]{2,20}[\\]})]|[-–—][A-Za-z0-9._]{2,20})[ \\t]*$',
      flags: 'gm',
    },
    defaultOn: false,
    help: 'Off by default: it also matches a one-word reply written after a dash.',
    falsePositives:
      'Eats a legitimate whole-line bracketed effect ("[SIGHS]") and a dash-led single-word ' +
      'reply ("—Never"). Ships OFF: the effect strippers already handle the first and the ' +
      'second is dialogue.',
  },
];

/** Everything, in one list, so the UI can render one table. */
export const ALL_PATTERNS: readonly PatternEntry[] = [
  ...MUSIC_PATTERNS,
  ...SOUND_EFFECT_PATTERNS,
  ...WATERMARK_PATTERNS,
];

/** The subset a preset enables by default. Every `defaultOn` here is DECISION_NEEDED. */
export function defaultOnPatterns(entries: readonly PatternEntry[]): readonly SerialRegex[] {
  return entries.filter((e) => e.defaultOn).map((e) => e.re);
}

export const DEFAULT_SOUND_EFFECT_PATTERNS: readonly SerialRegex[] =
  defaultOnPatterns(SOUND_EFFECT_PATTERNS);
export const DEFAULT_WATERMARK_PATTERNS: readonly SerialRegex[] =
  defaultOnPatterns(WATERMARK_PATTERNS);
export const DEFAULT_MUSIC_PATTERNS: readonly SerialRegex[] = defaultOnPatterns(MUSIC_PATTERNS);
